// Timer tool (Meeting Tools). Host sets a duration and controls it
// (start/pause/resume/reset/stop); attendees see a read-only readout. The live
// countdown also shows as a pill in the control bar for everyone. Visual only —
// reaching 0 never ends the meeting. Handlers are stable (useCallback); the
// per-second text comes from the shared isolated-tick hook, off the render path.

import { ChangeEvent, memo, MouseEvent, useCallback, useState } from 'react';

import { useCallStore } from '../../store/callStore';
import {
  pauseTimer,
  resetTimer,
  resumeTimer,
  startTimer,
  stopTimer,
  useTimerStore,
} from '../../store/timerStore';
import { useTimerText } from '../TimerPill';

const PRESETS = [1, 5, 10]; // minutes

function clamp(n: number, lo: number, hi: number): number {
  const v = Number.isFinite(n) ? Math.floor(n) : lo;
  return Math.max(lo, Math.min(hi, v));
}

export const TimerTool = memo(function TimerTool() {
  const isHost = useCallStore((s) => s.isHost);
  const status = useTimerStore((s) => s.status);
  const text = useTimerText();
  const [min, setMin] = useState(5);
  const [sec, setSec] = useState(0);

  const onMin = useCallback((e: ChangeEvent<HTMLInputElement>) => setMin(clamp(+e.target.value, 0, 599)), []);
  const onSec = useCallback((e: ChangeEvent<HTMLInputElement>) => setSec(clamp(+e.target.value, 0, 59)), []);
  const onPreset = useCallback((e: MouseEvent<HTMLButtonElement>) => {
    setMin(Number(e.currentTarget.dataset.min));
    setSec(0);
  }, []);
  const onStart = useCallback(() => startTimer((min * 60 + sec) * 1000), [min, sec]);
  const onPause = useCallback(() => pauseTimer(), []);
  const onResume = useCallback(() => resumeTimer(), []);
  const onReset = useCallback(() => resetTimer(), []);
  const onStop = useCallback(() => stopTimer(), []);

  if (!isHost) {
    return (
      <div className="bz-timer-tool">
        {status === 'off' ? (
          <p className="bz-mt-note">No timer is running. The host can start one.</p>
        ) : (
          <>
            <span className={`bz-timer-big bz-timer-${status}`}>{text}</span>
            <p className="bz-mt-note">The host controls the timer.</p>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="bz-timer-tool">
      {status === 'off' ? (
        <>
          <p className="bz-mt-note">
            A countdown everyone in the call can see. It&rsquo;s visual only — it won&rsquo;t end the
            meeting.
          </p>
          <div className="bz-timer-set">
            <label className="bz-timer-num">
              <input type="number" min={0} max={599} value={min} onChange={onMin} aria-label="Minutes" />
              <span>min</span>
            </label>
            <span className="bz-timer-colon">:</span>
            <label className="bz-timer-num">
              <input type="number" min={0} max={59} value={sec} onChange={onSec} aria-label="Seconds" />
              <span>sec</span>
            </label>
          </div>
          <div className="bz-timer-presets">
            {PRESETS.map((p) => (
              <button key={p} type="button" className="bz-timer-preset" data-min={p} onClick={onPreset}>
                {p} min
              </button>
            ))}
          </div>
          <button type="button" className="bz-poll-launch" onClick={onStart} disabled={min * 60 + sec <= 0}>
            Start
          </button>
        </>
      ) : (
        <>
          <span className={`bz-timer-big bz-timer-${status}`}>{text}</span>
          <div className="bz-timer-controls">
            {status === 'running' && (
              <button type="button" className="bz-poll-textbtn" onClick={onPause}>
                Pause
              </button>
            )}
            {status === 'paused' && (
              <button type="button" className="bz-poll-textbtn" onClick={onResume}>
                Resume
              </button>
            )}
            <button type="button" className="bz-poll-textbtn" onClick={onReset}>
              Reset
            </button>
            <button type="button" className="bz-poll-launch bz-timer-stop" onClick={onStop}>
              Stop
            </button>
          </div>
        </>
      )}
    </div>
  );
});
